<script setup>

import '@cta/frontend';
import '@campact/camper/core/CmprButton';
import '@campact/camper/core/CmprFormHint';
import '@campact/camper/cta/CmprPrefilledArea';
import CmprInput from '@cta/frontend/components/CmprInput.vue';
import CmprSegmentedControl from '@cta/frontend/components/CmprSegmentedControl.vue';

import appsignal from '@cta/shared/appsignal';
import { ref, computed, onMounted, useTemplateRef } from 'vue';
import csrfToken from '@cta/shared/csrf';
import logoutAndRedirect from '@cta/frontend/javascripts/logout';
import { debounce } from 'lodash/function';
import { inputToCents, numberToCurrency } from '@cta/frontend/javascripts/money';
import browserService from '@cta/frontend/services/BrowserService';
import trackingService from '@cta/frontend/services/TrackingService';
import { StripeService } from './services/StripeService';

class StripeError extends Error {
  constructor(message) {
    super(message);
    this.name = 'StripeError';
  }
}

const props = defineProps({
  selectableOneTimeAmounts: {
    type: Array,
    required: true,
  },
  selectableMonthlyAmounts: {
    type: Array,
    required: true,
  },
  showMonthlyToggle: {
    type: Boolean,
  },
  onlyMonthlyAllowed: {
    type: Boolean,
  },
  monthlyPreselected: {
    type: Boolean,
  },
  currency: {
    type: String,
    required: true,
  },
  stripePublishableKey: {
    type: String,
    required: true,
  },
  locale: {
    type: String,
    required: true,
  },
  defaultValues: {
    type: Object,
    default: () => ({}),
  },
  confirmPath: {
    type: String,
    required: true,
  },
  validatePath: {
    type: String,
    required: true,
  },
  logoutPath: {
    type: String,
    required: true,
  },
  redirectUrl: {
    type: String,
    required: true,
  },
  paymentMethodOrder: {
    type: Array,
    required: false,
    default: () => [],
  },
  prefillFormsAllowed: {
    type: Boolean,
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        firstname: {
          type: String,
          default: null,
        },
        lastname: {
          type: String,
          default: null,
        },
        emailaddress: {
          type: String,
          default: null,
        },
      };
    },
  },
  prefilledAmount: {
    type: String,
    default: null,
  },
  i18n: {
    type: Object,
    required: true,
  },
});

const form = useTemplateRef('form');
const amountInput = ref('');
const amountInputComponent = ref(null);
const publishableKey = props.stripePublishableKey;
const paymentElement = ref(null);
const paymentMode = computed(() => {
  return recurring.value ? 'subscription' : 'payment';
});

const currency = props.currency;
const locale = props.locale;
const selectedAmount = ref(null);
const recurring = ref(props.onlyMonthlyAllowed || props.monthlyPreselected);
const selectableAmounts = computed(() => {
  return recurring.value ? props.selectableMonthlyAmounts : props.selectableOneTimeAmounts;
});
const firstname = ref(props.prefilledAttributes.firstname);
const lastname = ref(props.prefilledAttributes.lastname);
const emailaddress = ref(props.prefilledAttributes.emailaddress);
const anythingPrefilled = computed(() => {
  return props.prefillFormsAllowed && (props.prefilledAttributes.firstname || props.prefilledAttributes.lastname || props.prefilledAttributes.emailaddress);
});

const buttonSelected = computed(() => {
  return selectableAmounts.value.includes(selectedAmount.value);
});
let errorsUnlocked = false;
let showPrefill = ref(false);
const submitting = ref(false);

let elements = null;
let stripe = null;
let stripeServerErrorMessage = ref(undefined);

const errors = ref({
  firstname: '',
  lastname: '',
  emailaddress: '',
  amount: '',
  recurring_contribution: '',
});

const submitButtonText = computed(() => {
  const period = recurring.value ? props.i18n.submit_recurring_contribution : props.i18n.submit_one_time_contribution;
  return period.replace('%{amount}', numberToCurrency(selectedAmount.value, locale, currency));
});

onMounted(async () => {
  selectedAmount.value = props.prefilledAmount ? inputToCents(props.prefilledAmount) : selectableAmounts.value[2];
  setAmountInputValue();

  if (props.prefillFormsAllowed) {
    await validate(true);
    if (errors.value.emailaddress || errors.value.firstname || errors.value.lastname) {
      errorsUnlocked = true;
    } else {
      showPrefill.value = true;
    }
  }

  const stripeService = new StripeService();
  const initializedStripe = await stripeService.setup({
    publishableKey,
    paymentMode: paymentMode.value,
    selectedAmount: selectedAmount.value,
    currency,
    locale,
    defaultValues: props.defaultValues,
    paymentMethodOrder: props.paymentMethodOrder,
    computedStyle: getComputedStyle(amountInputComponent.value.$el),
    onChange: hideStripeServerError,
    rootElement: paymentElement.value,
  });

  stripe = initializedStripe.stripe;
  elements = initializedStripe.elements;
});

function onAmountButtonClick(amount) {
  selectedAmount.value = amount;
  if (amountInput.value) {
    amountInput.value = '';
  }
}

function setAmountInputValue() {
  if (buttonSelected.value) {
    amountInput.value = '';
  } else {
    amountInput.value = numberToCurrency(selectedAmount.value, locale, currency);
  }
};

function onAmountInsert(event) {
  const inputValue = event.target.value;
  const amountInCents = inputToCents(inputValue);
  selectedAmount.value = amountInCents;

  throttledValidate();
}

function onRecurringChange() {
  // Both frequencies offer different amounts, so switching keeps the selected
  // amount but re-evaluates whether it is still one of the offered buttons or
  // belongs in the custom amount field.
  setAmountInputValue();
  updateElements();
}

function showStripeServerError(error) {
  stripeServerErrorMessage.value = error.message;

  appsignal.sendError(new StripeError(error.message), (span) => {
    span.setAction(error.type);
    span.setTags(error);
    span.setNamespace('stripe');
  });
}

function hideStripeServerError() {
  stripeServerErrorMessage.value = undefined;
}

const throttledValidate = debounce(async () => validate(), 500, { leading: true });

async function updateElements() {
  await elements?.update({ amount: selectedAmount.value, mode: paymentMode.value });
}

async function validate(prefilled = false) {
  if (!prefilled && !errorsUnlocked) {
    return;
  }

  const formData = new FormData();
  formData.append('contribution[firstname]', firstname.value || '');
  formData.append('contribution[lastname]', lastname.value || '');
  formData.append('contribution[emailaddress]', emailaddress.value || '');
  formData.append('contribution[amount]', selectedAmount.value || '');
  formData.append('contribution[recurring_contribution]', recurring.value || '');

  const response = await fetch(props.validatePath, {
    method: 'post',
    body: formData,
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
  });

  if (!response.ok) {
    browserService.reload();
    return;
  }

  const json = await response.json();
  const messages = new Map(Object.entries(json));

  errors.value = {
    firstname: messages.get('firstname') || '',
    lastname: messages.get('lastname') || '',
    emailaddress: messages.get('emailaddress') || '',
    amount: messages.get('amount') || '',
  };

  if (messages.size == 0) {
    await updateElements();
  }
}

async function commit() {
  errorsUnlocked = true;
  submitting.value = true;
  await validate();
  trySubmit();
}

async function trySubmit() {
  if (!errors.value.amount && !errors.value.emailaddress && !errors.value.firstname && !errors.value.lastname) {
    await submit();
  }
  setTimeout(() => {
    submitting.value = false;
  }, 1000);
}

async function submit() {
  const formData = new FormData();
  formData.append('contribution[amount]', selectedAmount.value);
  formData.append('contribution[recurring_contribution]', recurring.value);
  formData.append('contribution[firstname]', firstname.value);
  formData.append('contribution[lastname]', lastname.value);
  formData.append('contribution[emailaddress]', emailaddress.value);

  const json = await fetchConfirmation(formData);
  const { clientSecret, email, fullname } = json;

  const { error: submitError } = await elements.submit();
  if (submitError) return;

  trackingService.dispatchSendForm(form.value, {
    context: 'stripe-call',
    value: selectedAmount.value,
  });

  const { error } = await stripe.confirmPayment({
    elements,
    clientSecret,
    confirmParams: {
      return_url: props.redirectUrl,
      payment_method_data: { billing_details: { email, name: fullname } },
    },
  });

  // This point will only be reached if there is an immediate error when confirming the payment.
  // Otherwise, the customer will be redirected to `return_url`.
  showStripeServerError(error);
}

async function fetchConfirmation(formData) {
  const response = await fetch(props.confirmPath, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });

  const json = await response.json();

  if (response.ok) {
    return json;
  }

  browserService.reload();
}
</script>

<template>
  <form
    ref="form"
    class="stripe-call_form"
    data-analytics-id="stripe-call-form"
  >
    <div class="form-field">
      <div class="stripe-call_amount-selection">
        <cmpr-button
          v-for="(element, index) in selectableAmounts"
          :key="index"
          data-analytics-context="stripe-call"
          :data-analytics-id="`stripe-call-form-amount-button-${index + 1}`"
          :data-analytics-inner-text="element"
          size="medium"
          :variant.attr="selectedAmount === element ? 'primary' : 'secondary'"
          @click="onAmountButtonClick(element)"
        >
          {{ numberToCurrency(element, locale, currency) }}
        </cmpr-button>
        <CmprInput
          ref="amountInputComponent"
          v-model="amountInput"
          :class="{'is-invalid': errors.amount}"
          data-analytics-context="stripe-call"
          data-analytics-id="stripe-call-form-amount-field"
          data-analytics-inner-text="amount"
          :placeholder="props.i18n.attributes.amount"
          @blur="setAmountInputValue"
          @input="onAmountInsert"
        />
      </div>
      <cmpr-form-hint
        v-if="errors.amount"
        variant="error"
      >
        {{ errors.amount }}
      </cmpr-form-hint>
    </div>
    <div
      v-if="showMonthlyToggle"
      class="cmpr-segmented-control stripe-call-payment-interval-toggle"
      data-size="medium"
      data-variant="secondary"
    >
      <CmprSegmentedControl
        id="one_time_contribution"
        v-model="recurring"
        data-analytics-context="stripe-call"
        data-analytics-id="stripe-call-form-payment-interval-radio-one-time"
        data-analytics-inner-text="recurring"
        :value="false"
        @change="onRecurringChange()"
      />
      <label
        class="cmpr-segmented-control__label"
        for="one_time_contribution"
      >
        {{ props.i18n.one_time }}
      </label>
      <CmprSegmentedControl
        id="recurring_contribution"
        v-model="recurring"
        data-analytics-context="stripe-call"
        data-analytics-id="stripe-call-form-payment-interval-radio-monthly"
        data-analytics-inner-text="recurring"
        :value="true"
        @change="onRecurringChange()"
      />
      <label
        class="cmpr-segmented-control__label"
        for="recurring_contribution"
      >
        {{ props.i18n.monthly }}
      </label>
    </div>
    <input
      v-else-if="onlyMonthlyAllowed"
      v-model="recurring"
      type="hidden"
    >
    <div
      v-if="!showPrefill"
      class="stripe-call_personal-data-grid"
    >
      <a
        v-if="anythingPrefilled"
        class="stripe-call_form__logout-link"
        @click="logoutAndRedirect(logoutPath)"
      >
        {{ props.i18n.logout_link }}
      </a>
      <div class="stripe-call_name-grid">
        <div class="form-field">
          <CmprInput
            v-model="firstname"
            autocomplete="given-name"
            :class="{'is-invalid': errors.firstname}"
            data-analytics-context="stripe-call"
            data-analytics-id="stripe-call-form-firstname-field"
            data-analytics-inner-text="firstname"
            :placeholder="props.i18n.attributes.firstname"
            @input="throttledValidate"
            @keydown.enter="commit"
          />
          <cmpr-form-hint
            v-if="errors.firstname"
            variant="error"
          >
            {{ errors.firstname }}
          </cmpr-form-hint>
        </div>
        <div class="form-field">
          <CmprInput
            v-model="lastname"
            autocomplete="family-name"
            :class="{'is-invalid': errors.lastname}"
            data-analytics-context="stripe-call"
            data-analytics-id="stripe-call-form-lastname-field"
            data-analytics-inner-text="lastname"
            :placeholder="props.i18n.attributes.lastname"
            @input="throttledValidate"
            @keydown.enter="commit"
          />
          <cmpr-form-hint
            v-if="errors.lastname"
            variant="error"
          >
            {{ errors.lastname }}
          </cmpr-form-hint>
        </div>
      </div>
      <div class="form-field">
        <CmprInput
          v-model="emailaddress"
          autocomplete="email"
          :class="{'is-invalid': errors.emailaddress}"
          data-analytics-context="stripe-call"
          data-analytics-id="stripe-call-form-emailaddress-field"
          data-analytics-inner-text="emailaddress"
          :placeholder="props.i18n.attributes.emailaddress"
          @input="throttledValidate"
          @keydown.enter="commit"
        />
        <cmpr-form-hint
          v-if="errors.emailaddress"
          variant="error"
        >
          {{ errors.emailaddress }}
        </cmpr-form-hint>
      </div>
    </div>
    <div v-else>
      <a
        class="stripe-call_form__logout-link"
        @click="logoutAndRedirect(logoutPath)"
      >
        {{ props.i18n.logout_link }}
      </a>
      <cmpr-prefilled-area
        class="address-step__summary"
        variant="success"
      >
        <div class="fw-bold">
          {{ firstname }} {{ lastname }}
        </div>
        <span> {{ emailaddress }} </span>
      </cmpr-prefilled-area>
    </div>
    <div ref="paymentElement" />

    <cmpr-alert-banner
      v-if="stripeServerErrorMessage"
      variant="error"
    >
      {{ stripeServerErrorMessage }}
    </cmpr-alert-banner>

    <cmpr-button
      class="w-100 mt-60"
      data-analytics-context="stripe-call"
      data-analytics-id="stripe-call-form-submit-button"
      :disabled="submitting"
      size="large"
      variant="primary"
      @click.prevent="commit"
    >
      <cmpr-icon-solid-lock />
      {{ submitButtonText }}
    </cmpr-button>
  </form>
</template>

<style scoped lang="scss">

iframe:focus-visible {
  outline-width: 0;
}

.stripe-call_form {
  display: grid;
  gap: var(--cmpr-space-60);
}

.stripe-call_form__logout-link {
  cursor: pointer;
  font-size: var(--cmpr-font-size-30);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.stripe-call_amount-selection {
  display: grid;
  gap: var(--cmpr-space-60);
  grid-template-columns: repeat(3, 1fr);
}

.stripe-call-payment-interval-toggle {
  margin-bottom: var(--cmpr-space-60);
  margin-top: var(--cmpr-space-60);
}

.stripe-call_personal-data-grid {
  display: grid;
  gap: var(--cmpr-space-60);
  grid-template-columns: 1fr;
}

.stripe-call_name-grid {
  display: grid;
  gap: var(--cmpr-space-60);
  grid-template-columns: 1fr 1fr;
}
</style>
